import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, Facebook, Clock, Loader2, Send, CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { IowaMap } from "@/components/IowaMap";
import { CONTACT } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message sent! We'll be in touch soon.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const input = "w-full bg-transparent border-b border-white/20 focus:border-crate-yellow focus:outline-none text-white placeholder-white/30 py-3 transition-colors";

  return (
    <div data-testid="contact-page">
      <PageHero overline="Contact" title="Let's talk about your move." subtitle="Questions, custom quotes, or just saying hi — we'd love to hear from you." />

      <section className="mx-auto max-w-7xl px-6 md:px-12 pb-24 md:pb-32 grid lg:grid-cols-2 gap-12">
        {/* Form */}
        <div className="rounded-2xl bg-crate-surface border border-white/10 p-8 md:p-10">
          {done ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center text-center py-16" data-testid="contact-success">
              <CheckCircle2 className="h-16 w-16 text-crate-yellow" />
              <h3 className="font-heading font-bold text-2xl text-white mt-6">Thanks for reaching out!</h3>
              <p className="text-white/60 mt-3">We've received your message and will reply shortly.</p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-white/50 mb-2">Name *</label>
                <input name="name" value={form.name} onChange={onChange} required placeholder="Your name" className={input} data-testid="contact-name" />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-white/50 mb-2">Email *</label>
                  <input type="email" name="email" value={form.email} onChange={onChange} required placeholder="you@email.com" className={input} data-testid="contact-email" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-white/50 mb-2">Phone</label>
                  <input type="tel" name="phone" value={form.phone} onChange={onChange} placeholder="(712) 555-0000" className={input} data-testid="contact-phone" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-white/50 mb-2">Message *</label>
                <textarea name="message" value={form.message} onChange={onChange} required rows={4} placeholder="How can we help?" className={`${input} resize-none`} data-testid="contact-message" />
              </div>
              <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-crate-yellow text-crate-black font-bold px-8 py-4 hover:bg-white transition-colors disabled:opacity-60 w-full">
                {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending...</> : <>Send Message <Send className="h-4 w-4" /></>}
              </button>
            </form>
          )}
        </div>

        {/* Info */}
        <div className="space-y-8">
          <div className="grid sm:grid-cols-2 gap-4">
            <a href={CONTACT.phoneHref} className="rounded-2xl bg-crate-surface border border-white/10 p-6 hover:border-crate-yellow transition-colors" data-testid="contact-phone-card">
              <Phone className="h-6 w-6 text-crate-yellow" />
              <p className="text-white/50 text-xs uppercase tracking-wider mt-4">Phone</p>
              <p className="text-white font-semibold mt-1">{CONTACT.phone}</p>
            </a>
            <a href={`mailto:${CONTACT.email}`} className="rounded-2xl bg-crate-surface border border-white/10 p-6 hover:border-crate-yellow transition-colors" data-testid="contact-email-card">
              <Mail className="h-6 w-6 text-crate-yellow" />
              <p className="text-white/50 text-xs uppercase tracking-wider mt-4">Email</p>
              <p className="text-white font-semibold mt-1 break-all">{CONTACT.email}</p>
            </a>
            <a href={CONTACT.facebook} target="_blank" rel="noreferrer" className="rounded-2xl bg-crate-surface border border-white/10 p-6 hover:border-crate-yellow transition-colors" data-testid="contact-facebook-card">
              <Facebook className="h-6 w-6 text-crate-yellow" />
              <p className="text-white/50 text-xs uppercase tracking-wider mt-4">Facebook</p>
              <p className="text-white font-semibold mt-1">@colorcratemoving</p>
            </a>
            <div className="rounded-2xl bg-crate-surface border border-white/10 p-6">
              <Clock className="h-6 w-6 text-crate-yellow" />
              <p className="text-white/50 text-xs uppercase tracking-wider mt-4">Hours</p>
              <ul className="mt-2 space-y-1 text-sm">
                {CONTACT.hours.map((h) => (
                  <li key={h.day} className="text-white/70">{h.day}: <span className="text-white">{h.time}</span></li>
                ))}
              </ul>
            </div>
          </div>
          <IowaMap />
        </div>
      </section>
    </div>
  );
}
